import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { t, LanguageSwitcher } from '@fetmode/i18n';
import { isAlphaInviteOnlyUi, resolveAppUrl } from '@fetmode/app-core';
import { ColorModeToggle } from '@fetmode/theme';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth, useAuthModal } from '../../../features/auth/index.js';
import { homePathForMode } from '../../../features/experience/home-path-for-mode.js';
import { AgeVerifyBanner } from '../../../features/compliance/index.js';
import { useLocaleAccountSync } from '../../hooks/useLocaleAccountSync.js';
import { InLiveRoomProvider, useInLiveRoom } from '../../in-live-room-context.js';
import {
  isOnModelRoom,
  LiveStreamWaitProvider,
  roomPath,
  useLiveStreamWait,
} from '../../live-stream-wait-context.js';
import { SiteFooter } from '../SiteFooter/index.js';
import type { AppShellProps } from './AppShell.types.js';
import './AppShell.css';

const studioUrl = resolveAppUrl('studio', import.meta.env);

function buildNavItems(user: ReturnType<typeof useAuth>['user']): Array<{ to: string; label: string }> {
  const community = { to: '/community', label: t('common.nav.community') };
  const cams = { to: '/cams', label: t('common.nav.cams') };
  const videos = { to: '/videos', label: t('common.nav.videos') };
  const library = { to: '/library', label: t('common.nav.library') };
  const wallet = { to: '/wallet', label: t('common.nav.wallet') };
  const stats = { to: '/stats', label: t('common.nav.stats') };
  const settings = { to: '/settings', label: t('common.nav.settings') };

  if (!user) {
    return [cams, community, videos];
  }

  if (user.primaryExperienceMode === 'VIDEOS') {
    return [videos, cams, community, library, wallet, stats, settings];
  }

  if (user.primaryExperienceMode === 'COMMUNITY') {
    return [community, cams, videos, library, wallet, stats, settings];
  }

  return [cams, community, videos, library, wallet, stats, settings];
}

function isOverflowNavItem(to: string): boolean {
  return to === '/stats' || to === '/settings';
}

function isNavItemActive(pathname: string, to: string, memberHome: string | null): boolean {
  if (pathname === '/' && memberHome) {
    return to === memberHome;
  }
  if (to === '/cams') {
    return pathname === '/cams';
  }
  if (to === '/community') {
    return pathname === '/community';
  }
  if (to === '/videos') {
    return pathname === '/videos' || pathname.startsWith('/videos/');
  }
  if (to === '/wallet') {
    return pathname === '/wallet' || pathname.startsWith('/wallet/');
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

function navLinkClass(pathname: string, to: string, memberHome: string | null): string {
  const base = 'app-shell-nav__link';
  return isNavItemActive(pathname, to, memberHome) ? `${base} app-shell-nav__link--active` : base;
}

export function AppShell(props: AppShellProps): ReactElement {
  return (
    <InLiveRoomProvider>
      <LiveStreamWaitProvider>
        <AppShellChrome {...props} />
      </LiveStreamWaitProvider>
    </InLiveRoomProvider>
  );
}

function AppShellChrome({ children }: AppShellProps): ReactElement {
  const { user, logout } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { persistLocale } = useLocaleAccountSync();
  const { inLiveRoom } = useInLiveRoom();
  const { wait } = useLiveStreamWait();
  const location = useLocation();
  const navigate = useNavigate();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuToggleRef = useRef<HTMLButtonElement | null>(null);
  const isShowRoom = /^\/@[^/]+\/?$/.test(location.pathname);
  const isBrowseSurface =
    location.pathname === '/' ||
    location.pathname === '/cams' ||
    location.pathname === '/community' ||
    location.pathname === '/videos' ||
    location.pathname.startsWith('/videos/');
  const navItems = buildNavItems(user).filter((item) => !(inLiveRoom && item.to === '/wallet'));
  const memberHome =
    user && (user.role !== 'USER' || user.experienceOnboardingCompleted)
      ? homePathForMode(user.primaryExperienceMode)
      : null;
  const brandPath = '/';
  const primaryNavItems = navItems.filter((item) => !isOverflowNavItem(item.to));
  const overflowNavItems = ['/settings', '/stats']
    .map((to) => navItems.find((item) => item.to === to))
    .filter((item): item is { to: string; label: string } => item !== undefined);

  useEffect(() => {
    setAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!accountMenuOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (menuRef.current?.contains(target) || menuToggleRef.current?.contains(target)) {
        return;
      }
      setAccountMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [accountMenuOpen]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <a href="#main" className="skip-link">
        Skip to main content
      </a>
      <header className={user ? 'app-shell-header app-shell-header--authed' : 'app-shell-header'}>
        <Link to={brandPath} className="app-shell-brand">
          <img src="/brand/logo.png" alt="" width={30} height={30} aria-hidden />
          <span className="app-shell-brand__mark">{t('common.app.name')}</span>
        </Link>
        <nav aria-label="Primary" className="app-shell-nav">
          <ul className="app-shell-nav__list">
            {navItems.map((item) => (
              <li key={item.to} className={isOverflowNavItem(item.to) ? 'app-shell-nav__item--overflow' : undefined}>
                <Link
                  to={item.to}
                  className={navLinkClass(location.pathname, item.to, memberHome)}
                  aria-current={isNavItemActive(location.pathname, item.to, memberHome) ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li className="app-shell-nav__item--broadcast">
              <a href={studioUrl} className="app-shell-nav__link">
                {t('common.nav.broadcast')}
              </a>
            </li>
            {user ? (
              <li className="app-shell-nav__item--overflow">
                <button
                  type="button"
                  onClick={logout}
                  className="app-shell-nav__link app-shell-nav__link--action"
                >
                  {t('common.nav.signOut')}
                </button>
              </li>
            ) : (
              <>
                <li className="app-shell-nav__item--desktop-auth">
                  <button
                    type="button"
                    className="app-shell-nav__link"
                    onClick={() => {
                      openAuthModal({ mode: 'login' });
                    }}
                  >
                    {t('common.nav.signIn')}
                  </button>
                </li>
                <li className="app-shell-nav__item--desktop-auth">
                  {isAlphaInviteOnlyUi(import.meta.env) ? (
                    <Link className="app-shell-nav__link" to="/accept-invite">
                      {t('common.nav.haveInvite')}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="app-shell-nav__link"
                      onClick={() => {
                        openAuthModal({
                          mode: 'register',
                          intent: 'community',
                          onSuccess: () => {
                            void navigate('/onboarding/experience', { replace: true });
                          },
                        });
                      }}
                    >
                      {t('common.nav.signUp')}
                    </button>
                  )}
                </li>
                <li className="app-shell-nav__item--mobile-auth">
                  <button
                    type="button"
                    className="app-shell-nav__link"
                    onClick={() => {
                      openAuthModal({ intent: 'community' });
                    }}
                  >
                    {t('common.nav.logInJoin')}
                  </button>
                </li>
              </>
            )}
          </ul>
        </nav>
        <div className="app-shell-header__end">
          <div className="app-shell-header__end-tools">
            <LanguageSwitcher
              variant="compact"
              onLocaleChange={(next) => {
                persistLocale(next);
              }}
            />
            <ColorModeToggle
              labelToDark={t('common.theme.toDark')}
              labelToLight={t('common.theme.toLight')}
            />
          </div>
          {user ? (
            <button
              ref={menuToggleRef}
              type="button"
              className="app-shell-header__menu-toggle"
              aria-expanded={accountMenuOpen}
              aria-controls="app-shell-account-menu"
              aria-haspopup="true"
              onClick={() => {
                setAccountMenuOpen((open) => !open);
              }}
            >
              <span className="app-shell-header__menu-icon" aria-hidden />
              {accountMenuOpen ? t('common.nav.closeMenu') : t('common.nav.menu')}
            </button>
          ) : null}
        </div>
        {user && accountMenuOpen ? (
          <div
            ref={menuRef}
            id="app-shell-account-menu"
            className="app-shell-account-menu"
            role="navigation"
            aria-label={t('common.nav.menu')}
          >
            <ul className="app-shell-account-menu__list">
              {primaryNavItems.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`app-shell-account-menu__link ${navLinkClass(location.pathname, item.to, memberHome)}`}
                    aria-current={isNavItemActive(location.pathname, item.to, memberHome) ? 'page' : undefined}
                    onClick={() => {
                      setAccountMenuOpen(false);
                    }}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href={studioUrl}
                  className="app-shell-account-menu__link"
                  onClick={() => {
                    setAccountMenuOpen(false);
                  }}
                >
                  {t('common.nav.broadcast')}
                </a>
              </li>
              {overflowNavItems.map((item) => (
                <li key={item.to} className="app-shell-account-menu__account">
                  <Link
                    to={item.to}
                    className={`app-shell-account-menu__link ${navLinkClass(location.pathname, item.to, memberHome)}`}
                    aria-current={isNavItemActive(location.pathname, item.to, memberHome) ? 'page' : undefined}
                    onClick={() => {
                      setAccountMenuOpen(false);
                    }}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <li className="app-shell-account-menu__tools">
                <LanguageSwitcher
                  variant="compact"
                  onLocaleChange={(next) => {
                    persistLocale(next);
                  }}
                />
              </li>
              <li className="app-shell-account-menu__tools">
                <ColorModeToggle
                  labelToDark={t('common.theme.toDark')}
                  labelToLight={t('common.theme.toLight')}
                />
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setAccountMenuOpen(false);
                    logout();
                  }}
                  className="app-shell-account-menu__link app-shell-nav__link app-shell-nav__link--action"
                >
                  {t('common.nav.signOut')}
                </button>
              </li>
            </ul>
          </div>
        ) : null}
      </header>
      {wait && !isOnModelRoom(location.pathname, wait.username) ? (
        <div className="app-shell-wait-banner" role="status">
          <span>
            {t('common.show.playerWaitingBanner').replace('{{name}}', wait.displayName)}
          </span>
          <Link className="app-shell-wait-banner__link" to={roomPath(wait.username)}>
            {t('common.show.playerWaitingBannerBack')}
          </Link>
        </div>
      ) : null}
      <AgeVerifyBanner />
      <main
        id="main"
        className={
          isShowRoom
            ? 'app-main app-main--room'
            : isBrowseSurface
              ? 'app-main app-main--browse'
              : 'app-main'
        }
        style={{ flex: 1 }}
      >
        {children}
      </main>
      {location.pathname.startsWith('/onboarding') ? null : (
        <SiteFooter omitWallet={inLiveRoom} />
      )}
    </div>
  );
}
